import { takeEvery, call, put, select } from 'typed-redux-saga'
import { errorHandler, loadersSlice, notifyMessageCreator } from 'Modules'
import { setEntity } from 'Src/modules/page'
import { UserShortModel } from 'Pages/auth'
import { partnerApi, partnerSlice, partnerSelector, PartnerNS } from 'Pages/pp'
import {
	partnersSlice as partnerNewSlice,
	partnersSelector as partnersNewSelector,
} from 'Pages/partner'

function* getPartnerPrivateInfo(action: PartnerNS.GetPartnerPrivateInfo) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, tab, callback } = action.payload
		const res = yield* call(partnerApi.getPartnerPrivateInfoApi, id, tab)
		const entity = yield* select(partnerSelector.getEntitySelector)
		const oldDocuments = yield* select(partnersNewSelector.getDocuments)
		let newDocuments = null
		switch (tab) {
			case 'step1':
			case 'additional_info':
			case 'contact_details':
				yield* put(setEntity(Object.assign(entity, res)))
				callback?.(Object.assign(entity, res))
				break
			case 'step2':
				newDocuments = { ...oldDocuments, documents: res }
				yield* put(partnerNewSlice.actions.setDocuments(newDocuments))
				callback?.(newDocuments)
				break
			case 'contracts':
				newDocuments = { ...oldDocuments, contract_documents: res }
				yield* put(partnerNewSlice.actions.setDocuments(newDocuments))
				callback?.(newDocuments)
				break
			case 'archive':
				newDocuments = { ...oldDocuments, archive_documents: res }
				yield* put(partnerNewSlice.actions.setDocuments(newDocuments))
				callback?.(newDocuments)
				break
			default:
				break
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getPartnerPrivateInfoById(action: PartnerNS.GetPartnerPrivateInfoById) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, query, callback } = action.payload
		const res = yield* call(partnerApi.getPartnerInfoById, id, query)
		callback?.(res)
		yield* put(partnerSlice.actions.setPartnerFull(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getPartnerProducts(action: PartnerNS.GetPartnerProducts) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const id = action.payload
		const res = yield* call(partnerApi.getPartnerProducts, id)
		yield* put(partnerSlice.actions.setPartnerProducts(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* patchPartnerBlocking(action: PartnerNS.PatchPartnerBlocking) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const id = action.payload
		yield* call(partnerApi.patchPartnerBlocking, id)
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getPartnerActiveUsers(action: PartnerNS.GetPartnerActiveUsers) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const id = action.payload
		const users: UserShortModel[] = []
		for (let page = 1; ; page += 1) {
			const res = yield* call(partnerApi.getPartnerActiveUsers, id, page)
			users.push(...res.entries)
			if (!res.entries.length || users.length >= res.total_entries) {
				break
			}
		}
		yield* put(partnerSlice.actions.setPartnerActiveUsers(users))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* SendPartnerXmlToCrm(action: PartnerNS.SendPartnerXmlToCrm) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(partnerApi.SendPartnerXmlToCrm, action.payload)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.xml_to_crm_request.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* liquidatePartner(action: PartnerNS.LiquidatePartner) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(partnerApi.liquidatePartner, action.payload.id)
		yield* put(partnerSlice.actions.setPartnerFull(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		action.payload.callback?.()
		yield* call(notifyMessageCreator, 'common.partner_liquidated')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* revertLiquidation(action: PartnerNS.RevertLiquidation) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(partnerApi.revertLiquidation, action.payload.id)
		yield* put(partnerSlice.actions.setPartnerFull(res))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		action.payload.callback?.()
		yield* call(notifyMessageCreator, 'common.partner_revert_liquidation')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export const partnerSaga = function* saga() {
	yield* takeEvery(partnerSlice.actions.getPartnerPrivateInfo.type, getPartnerPrivateInfo)
	yield* takeEvery(partnerSlice.actions.getPartnerProducts.type, getPartnerProducts)
	yield* takeEvery(partnerSlice.actions.patchPartnerBlocking.type, patchPartnerBlocking)
	yield* takeEvery(partnerSlice.actions.getPartnerActiveUsers.type, getPartnerActiveUsers)
	yield* takeEvery(partnerSlice.actions.sendPartnerXmlToCrm.type, SendPartnerXmlToCrm)
	yield* takeEvery(partnerSlice.actions.getPartnerPrivateInfoById.type, getPartnerPrivateInfoById)
	yield* takeEvery(partnerSlice.actions.liquidatePartner.type, liquidatePartner)
	yield* takeEvery(partnerSlice.actions.revertLiquidation.type, revertLiquidation)
}
